import get from "lodash/get";
import groupBy from "lodash/groupBy";
import isEqual from "lodash/isEqual";
import PropTypes from "prop-types";
import React from "react";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import FormLabel from "react-bootstrap/FormLabel";
import FormControl from "react-bootstrap/FormControl";
import FormGroup from "react-bootstrap/FormGroup";
import FormText from "react-bootstrap/FormText";
import Row from "react-bootstrap/Row";
import { Link } from "react-router-dom";
import Select from "react-select";
import { BackButton } from "ui/button/back";
import { BasicLoader } from "ui/loader";
import { Notification } from "ui/notification";
import { getErrorMessage } from "util/response";
import { sortByLabelAsc } from "util/sort-by-prop";
import { Box } from "view/layout/box";
import { Page } from "view/layout/page";

class NewNetworkPage extends React.PureComponent {
  static get propTypes() {
    return {
      addNewNetwork: PropTypes.func.isRequired,
      fetchVistarAccountNetworks: PropTypes.func.isRequired,
      vistarAccountIds: PropTypes.array,
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      errorMessage: "",
      fetching: false,
      selectedVistarNetwork: null,
      submitted: false,
      successMessage: "",
      vistarNetworks: [],
    };

    this._onNetworkSelect = this._onNetworkSelect.bind(this);
    this._submit = this._submit.bind(this);
  }

  componentDidMount() {
    this._fetchVistarNetworks(this.props);
  }

  componentDidUpdate(prevProps) {
    const { vistarAccountIds } = prevProps;
    const vistarAccountIdsChanged = !isEqual(
      vistarAccountIds,
      this.props.vistarAccountIds,
    );

    if (vistarAccountIdsChanged) {
      this._fetchVistarNetworks(this.props);
    }
  }

  formatVistarNetworkLabel(vistarNetworkOpt) {
    const { friendlyLabel, value } = vistarNetworkOpt;

    return (
      <span>
        {friendlyLabel}
        <span className="ml-4 font-smaller text-muted text-monospace">
          {" "}
          ({value})
        </span>
      </span>
    );
  }

  _fetchVistarNetworks(props) {
    const { vistarAccountIds } = props;

    if (vistarAccountIds === null) {
      this.setState({ vistarNetworks: [] });
      return;
    }

    this.setState({ fetching: true }, this._doFetch.bind(this, props));
  }

  _doFetch(props) {
    const { fetchVistarAccountNetworks } = props;

    return fetchVistarAccountNetworks()
      .then((response) => {
        this.setState({ vistarNetworks: response.data.data });
      })
      .catch((err) => {
        this.setState({
          successMessage: "",
          errorMessage: getErrorMessage(err),
        });
      })
      .finally(() => {
        this.setState({ fetching: false });
      });
  }

  _onNetworkSelect(value) {
    this.setState({ selectedVistarNetwork: value });

    // Auto-fill name
    this.input.value = value ? value.friendlyLabel : "";
  }

  _getNetworkOption(network) {
    const { id, name, account_name: accountName, api_key: apiKey } = network;

    return {
      accountName,
      apiKey,
      friendlyLabel: name,
      label: `${name} (${id})`,
      value: id,
    };
  }

  _getAccountNetworkOptions() {
    const { vistarNetworks } = this.state;
    const accountNetworks = groupBy(vistarNetworks, "account_id");

    return Object.entries(accountNetworks)
      .map(([accountId, networks]) => {
        const networkOptions = networks
          .map(this._getNetworkOption)
          .sort(sortByLabelAsc);

        return {
          label: networkOptions[0].accountName,
          options: networkOptions,
        };
      })
      .sort(sortByLabelAsc);
  }

  _renderVistarNetworks() {
    const { fetching, selectedVistarNetwork } = this.state;
    if (fetching) {
      return <BasicLoader>Loading Vistar Networks...</BasicLoader>;
    }

    const networkOptions = this._getAccountNetworkOptions();
    if (networkOptions.length === 0) {
      return null;
    }

    return (
      <Row>
        <Col xs={12}>
          <FormLabel>Vistar Network</FormLabel>
          <Select
            name="vistarNetworkId"
            formatOptionLabel={this.formatVistarNetworkLabel}
            onChange={this._onNetworkSelect}
            options={networkOptions}
            placeholder={"Select Vistar Trafficking network (if applicable)"}
            required={false}
            value={selectedVistarNetwork}
          />
          <FormText>
            Please select the related Vistar Trafficking network if applicable.
          </FormText>
        </Col>
      </Row>
    );
  }

  _submit() {
    const { selectedVistarNetwork, vistarNetworks } = this.state;
    const selectedId = get(selectedVistarNetwork, "value", null);
    const vistarNetwork = vistarNetworks.find((net) => net.id === selectedId);

    const name = (this.input.value || "").trim();
    if (!name || name[0] === "_") {
      this.setState({
        errorMessage: "Invalid network name.",
        successMessage: "",
      });

      return;
    }

    const serviceArgs = {
      name,
      vistarNetwork,
    };

    this.setState(
      { submitted: true },
      this._submitNewNetwork.bind(this, serviceArgs),
    );
  }

  _submitNewNetwork(serviceArgs) {
    const { addNewNetwork } = this.props;

    return addNewNetwork(serviceArgs)
      .then(() => {
        this.setState({
          errorMessage: "",
          successMessage: "Network created successfully.",
        });
      })
      .catch((e) => {
        this.setState({
          errorMessage: getErrorMessage(e),
          successMessage: "",
        });
      })
      .finally(() => {
        this.setState({
          submitted: false,
        });
      });
  }

  _renderView() {
    const { errorMessage, fetching, submitted, successMessage } = this.state;

    return (
      <Row>
        <Col xs={6}>
          <Notification
            error={errorMessage}
            success={successMessage}
            successChildren={<Link to="/networks">Network List</Link>}
          />
          {this._renderVistarNetworks()}
          <Row>
            <Col xs={12}>
              <FormGroup controlId="name">
                <FormLabel>Network name</FormLabel>
                <FormControl
                  type="text"
                  placeholder="Network name"
                  disabled={fetching}
                  ref={(c) => {
                    this.input = c;
                  }}
                  required
                />
                <FormText>
                  Required. Network names cannot start with an underscore.
                </FormText>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <BackButton />
              <Button
                variant="primary"
                onClick={this._submit}
                disabled={fetching || submitted}
              >
                Create
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
    );
  }

  render() {
    const view = this._renderView();

    return (
      <Page title="New Network">
        <Box noHeader={true}>{view}</Box>
      </Page>
    );
  }
}

export { NewNetworkPage };
